import { router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import type { TouchEvent } from "react"

import type { DayOption, ImageItem } from "./ImagesSection"

export interface ImageOverlayLabels {
  overlayLabel: string
  // FR-10.6: "%{index} из %{total}", interpolated client-side
  // (`formatPosition` below) -- the `Images.tsx#formatUploadingLabel`
  // idiom, one shared shape for every client-interpolated format string
  // this domain sends.
  position: string
  close: string
  previous: string
  next: string
  delete: string
  deleteConfirm: string
  // t031 impl-mode (FR-1.2): the days pill's two forms -- `daysPill`
  // carries an unresolved `%{count}` placeholder (the `position` idiom),
  // `daysPillEmpty` is the whole string at N = 0.
  daysPill: string
  daysPillEmpty: string
  // t031 impl-mode (FR-2.1): days mode's own title and its «Готово»
  // exit, both plain strings -- no interpolation.
  daysModeTitle: string
  daysModeDone: string
  // t031 impl-mode (FR-2.2): "%{name} · %{date}" -- interpolated
  // client-side from the picture's own `uploaderName`/`stampText`,
  // already present in its props, the same `position`/`daysPill` idiom.
  daysSubtitle: string
}

// t031 impl-mode (FR-2.4): the overlay's second mode -- `days`, entered
// only from `picture` (the pill) and left only by closing the overlay
// (FR-2.3, FR-4.3's "one-way" ruling). No third mode, no route.
type OverlayMode = "picture" | "days"

interface ImageOverlayProps {
  // FR-2.2: the overlay walks the exact same server-ordered array the
  // grid renders -- no second ordering exists anywhere on the client, so
  // the grid's visual order and the overlay's traversal order are
  // identical by construction.
  images: ImageItem[]
  index: number
  // t031 impl-focus-docs (FR-7.2): `deleted` distinguishes a successful
  // delete from every other exit (×, «Готово», Escape) -- `ImagesSection`
  // needs to know which happened, since only a delete leaves the closing
  // picture's own grid cell gone, requiring a different refocus rule
  // (below) than the ordinary "focus the cell that opened this" one.
  onClose: (deleted?: boolean) => void
  onNavigate: (index: number) => void
  labels: ImageOverlayLabels
  // t031 impl-mode (FR-3.1, FR-3.4, FR-3.5): the trip's day list for
  // days mode's own row list -- the same bundle on both entry points,
  // composed server-side by `Schedule::ListDayOptions`.
  days: DayOption[]
}

const SWIPE_THRESHOLD_PX = 50

function formatPosition(template: string, index: number, total: number): string {
  return template.replace("%{index}", String(index)).replace("%{total}", String(total))
}

// t031 impl-mode (FR-1.2): "Дни · N", or the bare `daysPillEmpty` form at
// N = 0 -- the design's own "no dangling · 0" ruling.
function formatDaysPill(labels: ImageOverlayLabels, dayCount: number): string {
  return dayCount === 0 ? labels.daysPillEmpty : labels.daysPill.replace("%{count}", String(dayCount))
}

// t031 impl-mode (FR-2.2): "{uploader} · {date}", both values already
// resolved server-side on the picture itself -- no date formatting here.
// One callback-based pass (PR #36 review): the uploader's name is user
// text, so it must land literally -- a plain `replace` would read `$&`
// in it as a replacement pattern, and a second pass would re-scan it.
function formatDaysSubtitle(template: string, image: ImageItem): string {
  const values: Record<string, string> = { name: image.uploaderName, date: image.stampText }
  return template.replace(/%\{(name|date)\}/g, (_match, key: string) => values[key])
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function PreviousIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M9.5 4L5.5 8l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function NextIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6.5 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// t031 impl-mode (FR-1.2, images.html:314): the pill's own calendar
// glyph, verbatim from the contract.
function CalendarIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="3.5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2.5 7h11M5.5 2v3M10.5 2v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

// t031 impl-mode (images.html:374): a checked row's own white tick.
function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6.5l2.5 2.5 4.5-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ARROW_BASE_CLASSES =
  "flex h-9 w-9 items-center justify-center rounded-full border border-white/20"

interface DayRowProps {
  day: DayOption
  checked: boolean
  isLast: boolean
  onToggle: () => void
}

// t031 impl-mode (FR-3.2, FR-3.3, images.html:361-423): one row of the
// days-mode list -- the day's short date, then its label-or-weekday text
// (already composed server-side, `Schedule::ListDayOptions`), then the
// checked state's own round tick. `role="checkbox"` + `aria-checked` on
// the row's own button, not a native `<input>` -- the design's own
// control shape. The last row alone closes the list with a bottom
// border too (`border-y`), matching the contract's own last-row rule.
//
// t031 impl-tap (FR-4.1 client half, AC-5): a tap attaches or detaches
// at once -- `onToggle` alone, no local state of its own; the checked
// set this row renders from lives one level up, in `ImageOverlay`
// itself (FR-5.5, FR-5.6).
//
// The design's own two-shade unchecked text (a labelled day dimmer than
// an unlabelled/weekday one) is not reproduced: the row's text is one
// opaque string from the server (`Schedule::ListDayOptions`'s own
// `label_or_weekday`), with no flag distinguishing the two cases in the
// props this child's own upstream slice landed -- a disclosed, narrow
// simplification (one muted shade for every unchecked row) rather than
// a reach back into that slice's already-settled shape.
function DayRow({ day, checked, isLast, onToggle }: DayRowProps) {
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left border-white/10 ${
          isLast ? "border-y" : "border-t"
        }`}
        onClick={onToggle}
      >
        <span className="w-[52px] shrink-0 text-[15px] font-semibold leading-5 tabular-nums">
          {day.shortDate}
        </span>
        <span
          className={`min-w-0 flex-1 truncate text-sm leading-5 ${
            checked ? "text-white" : "text-gray-300"
          }`}
        >
          {day.text}
        </span>
        {checked ? (
          <span
            aria-hidden="true"
            className="flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-accent bg-accent"
          >
            <CheckIcon />
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="size-[22px] shrink-0 rounded-full border-[1.5px] border-white/30"
          />
        )}
      </button>
    </li>
  )
}

// The full-screen overlay — docs/design/images.html Block 4 implemented
// verbatim, «C без зума» (no pinch/double-tap zoom, no thumbnail strip,
// both deferred). A component in `pages/`, not an Inertia page of its
// own (the `links/pages/LinkPills.tsx` precedent) -- `Images.tsx` owns
// which index is open (component state, not a route: navigating away
// closes it by unmount) and renders this conditionally.
//
// FR-10.6: focus moves into the overlay on open (the close button, on
// mount) and back to the originating cell on close -- `Images.tsx`'s own
// job, since it alone knows which cell triggered the open. Background
// scroll is blocked for the overlay's whole mounted lifetime and
// restored on unmount. Both effects below intentionally run once (empty
// dependency array): neither should re-fire while navigating between
// images, only on the overlay's own mount/unmount.
//
// t031 impl-mode (FR-2.4, Decision 1): `mode` is `picture | days` on this
// one component -- no route, no second modal, no change to the address
// bar. `daysSnapshot` is the picture days mode entered with, taken once
// on the flip (Decision 2, Decision 12): days mode reads it, and only
// it, for every picture-identifying fact it needs (id, uploader, upload
// date) -- never `images[index]` -- so a write this child adds can
// shrink or reorder `images` under the overlay without days mode ever
// reading a now-wrong array slot. It is not refreshed from `items` on a
// later render (plan Child 3's own ruling): the only facts it is read
// for never change.
//
// t031 impl-tap (FR-5.4, FR-5.5, FR-5.6): the checked set itself is a
// **separate** piece of state, `dayIds` -- seeded from the picture's own
// `dayIds` prop the moment the overlay enters days mode, and from the
// first write onward it is the set the newest request carries, never
// cleared and never re-read from props or from `daysSnapshot` again.
// That is what survives FR-5.5: once a day-page overlay detaches the
// day it opened from, the picture drops out of the page's own props
// entirely, so neither fresh props nor the flip-time snapshot can be
// trusted any more -- only the last-sent set can.
export default function ImageOverlay({ images, index, onClose, onNavigate, labels, days }: ImageOverlayProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  // t031 impl-focus-docs (FR-7.1, AC-11 fix): the flip's own close button
  // -- a *different* DOM node from `closeButtonRef` above (a separate
  // element in the days-mode branch, not a re-render of the same one).
  // The pill that was just clicked unmounts the instant `mode` becomes
  // "days" (it renders only in the picture-mode branch), taking focus
  // down with it; without this, activeElement falls to `document.body`
  // and the very next Tab matches neither of `trapFocus`'s own boundary
  // checks, so the browser's native tab order -- unconfined at that
  // point -- walks focus into the page behind the overlay.
  const daysCloseButtonRef = useRef<HTMLButtonElement>(null)
  // t031 impl-focus-docs (FR-7.1): the dialog's own node -- queried fresh
  // on every Tab press (never memoized) so the trap always sees whichever
  // controls the *current* mode actually rendered, with no second copy of
  // this logic for days mode.
  const dialogRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number | null>(null)
  const [mode, setMode] = useState<OverlayMode>("picture")
  const [daysSnapshot, setDaysSnapshot] = useState<ImageItem | null>(null)
  // t031 impl-tap (FR-5.6): `dayIds` is the rendered truth, `dayIdsRef`
  // its synchronous mirror -- a `PUT`'s `onFinish` callback fires well
  // after the render that scheduled it, so it reads the ref (always
  // current) rather than a closed-over `dayIds` value (possibly stale
  // by then). `setCheckedDayIds` is the only way either one changes, so
  // they can never drift apart.
  const [dayIds, setDayIds] = useState<number[]>([])
  const dayIdsRef = useRef<number[]>([])
  // t031 impl-tap (FR-5.6): coalescing -- at most one write in flight at
  // a time. A tap made while one is already in flight only raises
  // `queuedRef`; the settle handler below is what actually sends it, so
  // a burst of taps against one in-flight request produces exactly one
  // further request, carrying every tap's own contribution to `dayIds`.
  const inFlightRef = useRef(false)
  const queuedRef = useRef(false)
  const image = images[index]
  const previous = index > 0 ? images[index - 1] : null
  const next = index < images.length - 1 ? images[index + 1] : null

  function setCheckedDayIds(next: number[]) {
    dayIdsRef.current = next
    setDayIds(next)
  }

  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  // t031 impl-focus-docs (FR-7.1, AC-11 fix): mirrors the mount effect
  // above for the flip -- the pill that triggered `mode === "days"`
  // unmounts in the same render that days mode's own chrome appears, so
  // focus is deliberately moved to that chrome's own close button rather
  // than left to fall wherever the browser puts it once the focused node
  // is gone. Runs only on the `picture` → `days` transition (the flip is
  // one-way, so `mode` never becomes `"days"` a second time on one mount).
  useEffect(() => {
    if (mode === "days") daysCloseButtonRef.current?.focus()
  }, [mode])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // FR-10.6: ←/→/Escape, wired only while this component is mounted (the
  // Layout.tsx menu-dismissal effect shape) and torn down on unmount --
  // never left listening once the overlay has closed. Depends on
  // `index`/`images.length` so a stale closure never fires the previous
  // render's boundary check.
  //
  // t031 impl-mode (FR-2.5): ←/→ move nothing while in days mode -- the
  // walking position is frozen for as long as the overlay stays there
  // (Decision 12); Escape still closes the overlay in either mode, the
  // same as × and «Готово». t031 impl-focus-docs (FR-7.1, AC-11): the
  // Tab/Shift+Tab focus trap runs ahead of the days-mode short-circuit --
  // one handler, cycling in both modes, since it queries the dialog's own
  // node (`focusableElements`, below) fresh on every press rather than
  // keeping a mode-specific list of its own. `dialogRef`/`mode` join the
  // dependency array so the trap's own closures never see a stale node or
  // a stale mode -- both are nested here (not top-level component
  // functions) purely so the linter's exhaustive-deps rule never flags
  // them as re-created-every-render dependencies of their own.
  useEffect(() => {
    function focusableElements(): HTMLElement[] {
      if (!dialogRef.current) return []

      return Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      )
    }

    // t031 impl-focus-docs (FR-7.1, AC-11): Tab/Shift+Tab cycle within the
    // dialog alone -- wrapping at each end -- so focus never reaches the
    // page behind it. Only the two boundary presses are intercepted
    // (`preventDefault` + an explicit `.focus()`); every press in between
    // is the browser's own native tab order, already confined to the
    // dialog's own controls since nothing outside it competes for that
    // order while the overlay is the last thing in the DOM.
    //
    // Defensive (AC-11 fix): if `document.activeElement` is not inside
    // the dialog at all -- its own control unmounted out from under it
    // (the mode-flip effect above is what actually prevents this for the
    // one known case, the pill), or `document.body` some other way --
    // there is no "current position" to advance from, so this Tab is
    // treated as an escape attempt and redirected to the first control,
    // regardless of Shift.
    function trapFocus(event: KeyboardEvent) {
      const elements = focusableElements()
      if (elements.length === 0) return

      const first = elements[0]
      const last = elements[elements.length - 1]

      if (!dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose()
        return
      }
      if (event.key === "Tab") {
        trapFocus(event)
        return
      }
      if (mode === "days") return
      if (event.key === "ArrowLeft" && index > 0) onNavigate(index - 1)
      else if (event.key === "ArrowRight" && index < images.length - 1) onNavigate(index + 1)
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [index, images.length, mode, onClose, onNavigate])

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    touchStartX.current = event.touches[0]?.clientX ?? null
  }

  // A plain touchstart/touchend horizontal delta >= 50px -- no library
  // (FR-10.6). A vertical or short touch (a tap opening nothing, a
  // scroll attempt) moves nothing. t031 impl-mode (FR-2.5): inert in
  // days mode, the ←/→ key handler's own ruling.
  function handleTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const startX = touchStartX.current
    touchStartX.current = null
    if (startX === null || mode === "days") return

    const endX = event.changedTouches[0]?.clientX
    if (endX === undefined) return

    const delta = endX - startX
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return
    if (delta > 0 && index > 0) onNavigate(index - 1)
    else if (delta < 0 && index < images.length - 1) onNavigate(index + 1)
  }

  // t030 impl-confirm (FR-6.1, FR-6.2): the confirm's second line, added
  // only when the picture is attached to at least one day -- byte-
  // identical to t028's one-line confirm at `dayCount` 0.
  function confirmMessage(): string {
    return image.dayCount > 0 ? `${labels.deleteConfirm}\n${image.attachedText}` : labels.deleteConfirm
  }

  // FR-9.3: behind the system window.confirm -- the `AdminContactForm`
  // delete idiom.
  function handleDelete() {
    if (!window.confirm(confirmMessage())) return

    // FR-9.5: on success the overlay closes and the grid re-renders from
    // this visit's own fresh props -- no client-side splice of the
    // deleted row. t031 impl-focus-docs (FR-7.2): `preserveState` keeps
    // `ImagesSection` mounted across the visit (the `runBatch` precedent
    // above) so its own post-delete focus rule -- below, in that
    // component -- has a surviving instance to run on; `onClose(true)`
    // is what tells it this exit was a delete, not an ordinary close.
    router.delete(`/images/${image.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onClose(true),
    })
  }

  // t031 impl-mode (FR-1.3, Decision 3): the pill's own tap -- one-way,
  // there is no control back to picture mode anywhere in this component.
  // t031 impl-tap (FR-5.6): `dayIds` is seeded here, from props, at the
  // exact moment of the flip -- its only seeding, ever, for this mount.
  function openDaysMode() {
    setDaysSnapshot(image)
    setCheckedDayIds(image.dayIds)
    setMode("days")
  }

  // t031 impl-tap (FR-4.1 client half, FR-5.6, AC-5, AC-15): a JSON body
  // -- never `forceFormData` -- carrying the picture's whole intended
  // day_ids set, so an empty set survives transmission. `preserveState`
  // keeps the overlay mounted (and so `dayIds`/`daysSnapshot`/`mode`
  // intact) across the visit; `preserveScroll` keeps the page from
  // jumping. `onFinish` is the coalescing handler: it lowers
  // `inFlightRef` and, if a tap arrived while this request was in
  // flight, immediately sends one further request carrying whatever
  // `dayIdsRef` holds *now* -- every tap made in the meantime, not only
  // the one that raised `queuedRef` -- so a burst of taps against one
  // in-flight write costs exactly one more request, never one per tap.
  function sendDayIds(pictureId: number, ids: number[]) {
    inFlightRef.current = true
    router.put(
      `/images/${pictureId}/days`,
      { day_ids: ids },
      {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => {
          inFlightRef.current = false
          if (queuedRef.current) {
            queuedRef.current = false
            sendDayIds(pictureId, dayIdsRef.current)
          }
        },
      },
    )
  }

  // t031 impl-tap (FR-5.6, AC-5, AC-15): toggles `dayId` against the
  // current set and renders the result immediately, before the response
  // ever lands (FR-5.4's "the set the server stored" and the set just
  // sent are the same set, by construction, once the request succeeds).
  // At most one write is ever in flight: a tap made while one already is
  // only raises `queuedRef`, coalescing into the settle handler's own
  // follow-up request above -- no tap is dropped and none silently
  // undoes the one before it.
  function toggleDay(pictureId: number, dayId: number) {
    const current = dayIdsRef.current
    const next = current.includes(dayId)
      ? current.filter((id) => id !== dayId)
      : [...current, dayId]

    setCheckedDayIds(next)

    if (inFlightRef.current) {
      queuedRef.current = true
      return
    }

    sendDayIds(pictureId, next)
  }

  const inDaysMode = mode === "days" && daysSnapshot !== null

  // Owner ruling 2026-09-13 (images.html «Days mode on a wide screen»,
  // artboard 10.e): in days mode the root is the dimmed backdrop and an
  // inner panel is the black sheet -- the whole screen on a phone, a
  // centered `max-w-xl` card (the page column's own width) with rounded
  // corners from `sm:` up. Picture mode keeps t031's root classes
  // exactly: the letterboxed full variant needs the whole screen. The
  // `role="dialog"` root is the same element in both modes, so the focus
  // trap, Escape and the touch handlers above stay where they were; a
  // click on the backdrop closes nothing (FR-2.3: «Готово» and × are the
  // only exits).
  const rootClasses = inDaysMode
    ? "fixed inset-0 z-50 flex items-center justify-center bg-black/80 sm:p-8"
    : "fixed inset-0 z-50 flex flex-col bg-black text-white"

  return (
    <div
      ref={dialogRef}
      className={rootClasses}
      role="dialog"
      aria-modal="true"
      aria-label={inDaysMode ? labels.daysModeTitle : labels.overlayLabel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {inDaysMode ? (
        <div className="flex h-full w-full flex-col bg-black text-white sm:h-auto sm:max-h-full sm:max-w-xl sm:rounded-2xl">
          {/* t031 impl-mode (FR-2.1, images.html:351-360): title over the
              subtitle, ×, no pill -- the flip's whole top bar. */}
          <div className="flex items-center justify-between gap-3 px-4 py-3.5">
            <div className="flex min-w-0 flex-col">
              <p className="text-[15px] font-semibold leading-5">{labels.daysModeTitle}</p>
              <p className="text-xs leading-4 text-gray-400">
                {formatDaysSubtitle(labels.daysSubtitle, daysSnapshot)}
              </p>
            </div>
            <button
              ref={daysCloseButtonRef}
              type="button"
              aria-label={labels.close}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10"
              onClick={() => onClose()}
            >
              <CloseIcon />
            </button>
          </div>
          {/* t031 impl-mode (FR-3.1, FR-3.6): every trip day, oldest
              first -- an empty trip renders an empty list, no rows and
              no new empty-state copy. */}
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {days.map((day, dayIndex) => (
              <DayRow
                key={day.id}
                day={day}
                checked={dayIds.includes(day.id)}
                isLast={dayIndex === days.length - 1}
                onToggle={() => toggleDay(daysSnapshot.id, day.id)}
              />
            ))}
          </ul>
          <div className="px-4 pb-6 pt-3.5">
            {/* FR-2.3: «Готово» closes the overlay outright, the same as
                × -- neither returns to picture mode. */}
            <button
              type="button"
              className="flex h-11 w-full items-center justify-center rounded-xl bg-accent text-[15px] font-semibold text-white"
              onClick={() => onClose()}
            >
              {labels.daysModeDone}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 px-4 py-3.5">
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="text-[15px] font-semibold leading-5">{image.uploaderName}</p>
              <p className="text-xs leading-4 text-gray-400">{image.stampText}</p>
            </div>
            {/* t031 impl-mode (FR-1.1, images.html:313-316): unlike
                «Удалить» below, never permission-gated -- rendered and
                enabled for every signed-in viewer. */}
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-white/10 px-3 py-1.5 text-[13px] font-medium leading-[18px]"
              onClick={openDaysMode}
            >
              <CalendarIcon />
              {formatDaysPill(labels, image.dayCount)}
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={labels.close}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10"
              onClick={() => onClose()}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <img src={image.fullUrl} alt={image.alt} className="max-h-full max-w-full object-contain" />
          </div>
          <div className="flex items-center justify-between gap-3 px-4 pb-6 pt-3.5">
            {/* FR-9.6: rendered only when the server said this viewer may
                delete this image -- otherwise the contract's own empty
                placeholder, so the counter and arrows keep their slots. */}
            {image.canDelete ? (
              <button
                type="button"
                className="text-[15px] font-medium text-red-300"
                onClick={handleDelete}
              >
                {labels.delete}
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
            <p className="text-[13px] text-gray-400 tabular-nums">
              {formatPosition(labels.position, index + 1, images.length)}
            </p>
            <div className="flex gap-2">
              {/* FR-10.6: the arrow at either end is dimmed and inert
                  (opacity-30, disabled), never hidden -- the day-page ruling
                  transplanted verbatim. */}
              <button
                type="button"
                aria-label={labels.previous}
                disabled={previous === null}
                className={`${ARROW_BASE_CLASSES} ${previous === null ? "opacity-30" : ""}`}
                onClick={() => onNavigate(index - 1)}
              >
                <PreviousIcon />
              </button>
              <button
                type="button"
                aria-label={labels.next}
                disabled={next === null}
                className={`${ARROW_BASE_CLASSES} ${next === null ? "opacity-30" : ""}`}
                onClick={() => onNavigate(index + 1)}
              >
                <NextIcon />
              </button>
            </div>
          </div>
          {/* FR-10.6: both neighbours' full variants are preloaded as hidden
              <img> elements -- zero, one or two of them, depending on
              whether this index sits at either end of the array. */}
          {previous && <img src={previous.fullUrl} alt="" aria-hidden="true" className="hidden" />}
          {next && <img src={next.fullUrl} alt="" aria-hidden="true" className="hidden" />}
        </>
      )}
    </div>
  )
}
